"use client";

import { useAuth } from "@/hooks/use-auth";
import { useBuyerStoreData } from "@/providers/global";
import { useUserPreferencesStore } from "@/stores/user-preferences";
import { useEffect, useMemo } from "react";

type Relationship = "buyer" | "seller";

export interface UserGroup {
	id: string;
	relationship: string;
	name?: string;
	type?: string;
}

/**
 * Hook for relationship detection and group ID selection.
 *
 * - If single group for relationship → auto-select it
 * - If multiple groups → returns undefined, triggering GroupSelectionGuard
 * - Validates stored preference matches current relationship
 * - Falls back to global stores (buyerStore/sellerStore) when user.groups is empty
 * - Syncs preferences store when group ID is determined from fallback
 */
export function useRelationship() {
	const { user, isAuthenticated, isLoading } = useAuth();
	const selectedGroupId = useUserPreferencesStore(state => state.selectedGroupId);
	const storedRelationship = useUserPreferencesStore(state => state.relationship);
	const setSelectedGroup = useUserPreferencesStore(state => state.setSelectedGroup);

	// Get IDs from global stores as fallback (synced from useAuth at login)
	const buyerIdFromStore = useBuyerStoreData(state => state.buyerId);

	// Groups for each relationship from user data
	const buyerGroups = useMemo(() => {
		return (user?.groups?.filter((g: UserGroup) => g.relationship === "buyer") || []) as UserGroup[];
	}, [user?.groups]);

	const sellerGroups = useMemo(() => {
		return (user?.groups?.filter((g: UserGroup) => g.relationship === "seller") || []) as UserGroup[];
	}, [user?.groups]);

	// Current relationship from user data
	const currentRelationship = useMemo((): Relationship => {
		const relation = user?.relation as Relationship | undefined;
		if (relation === "buyer" || relation === "seller") return relation;

		const firstGroup = Array.isArray(user?.groups) ? user.groups[0] : undefined;
		const firstGroupRelation = firstGroup?.relationship as Relationship | undefined;
		if (firstGroupRelation === "buyer" || firstGroupRelation === "seller") return firstGroupRelation;

		return "buyer";
	}, [user?.relation, user?.groups]);

	// Get group ID for a relationship with proper validation
	const getValidGroupId = (rel: Relationship): string | undefined => {
		const groups = rel === "buyer" ? buyerGroups : sellerGroups;
		const storeId = rel === "buyer" ? buyerIdFromStore : undefined;

		// Priority 1: Use store ID if available (most reliable source)
		if (storeId) return storeId;

		// Priority 2: Use stored preference if it matches and exists in groups
		if (selectedGroupId && storedRelationship === rel) {
			const isValid = groups.some(g => g.id === selectedGroupId);
			if (isValid) return selectedGroupId;
		}

		// Priority 3: Auto-select if single group
		if (groups.length === 1) return groups[0].id;

		// Multiple groups with no valid selection → undefined (triggers guard)
		return undefined;
	};

	const buyerGroupId = getValidGroupId("buyer");
	const sellerGroupId = getValidGroupId("seller");

	// Get active group ID based on current relationship
	const activeGroupId = currentRelationship === "buyer" ? buyerGroupId : sellerGroupId;

	// Sync preferences store when group ID is determined but differs from stored value
	useEffect(() => {
		if (!isLoading && isAuthenticated && activeGroupId) {
			// Only sync if the stored value is different
			if (selectedGroupId !== activeGroupId || storedRelationship !== currentRelationship) {
				setSelectedGroup({ selectedGroupId: activeGroupId, relationship: currentRelationship });
			}
		}
	}, [
		isLoading,
		isAuthenticated,
		activeGroupId,
		selectedGroupId,
		currentRelationship,
		storedRelationship,
		setSelectedGroup,
	]);

	return {
		currentRelationship,
		isBuyer: currentRelationship === "buyer",
		isSeller: currentRelationship === "seller",
		buyerGroupId,
		sellerGroupId,
		// For group selection guard
		buyerGroups,
		sellerGroups,
		needsGroupSelection: (rel: Relationship) => {
			const groups = rel === "buyer" ? buyerGroups : sellerGroups;
			const groupId = rel === "buyer" ? buyerGroupId : sellerGroupId;
			return groups.length > 1 && !groupId;
		},
		user,
		isAuthenticated,
		isLoading,
	};
}
